// adminfrontend/src/admin_ui/pages/AdminShipmentDetailPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import adminLogisticsApi from "../services/adminLogisticsApi";
import "../styles/Admin.css";

function fmtTS(ts) {
  if (!ts) return "-";
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return String(ts);
    return d.toISOString().replace("T", " ").slice(0, 19);
  } catch {
    return String(ts);
  }
}

function pickObj(data, keys) {
  if (!data || data.ok !== true) return null;
  for (const k of keys) {
    const v = data[k];
    if (v && typeof v === "object" && !Array.isArray(v)) return v;
  }
  return null;
}

function pickList(data, keys) {
  if (!data || data.ok !== true) return [];
  for (const k of keys) {
    const v = data[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

function AdminShipmentDetailPage() {
  const { id } = useParams();

  const [shipment, setShipment] = useState(null);
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);

  const [eventType, setEventType] = useState("NOTE");
  const [locationText, setLocationText] = useState("");
  const [notes, setNotes] = useState("");

  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const normalizedItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const normalizedEvents = useMemo(() => (Array.isArray(events) ? events : []), [events]);

  async function load() {
    setLoading(true);
    setError("");
    setOkMsg("");
    try {
      const data = await adminLogisticsApi.getShipment(id);
      if (!data || data.ok !== true) throw new Error("detail_failed");

      const sh = pickObj(data, ["shipment"]) || data;
      setShipment(sh);

      const it = pickList(data, ["items", "shipmentItems", "lines"]);
      const ev = pickList(data, ["events", "shipmentEvents", "timeline"]);

      // if backend returns embedded fields, tolerate that too
      setItems(it.length ? it : (Array.isArray(sh?.items) ? sh.items : []));
      setEvents(ev.length ? ev : (Array.isArray(sh?.events) ? sh.events : []));
    } catch (e) {
      console.error("[AdminShipmentDetailPage] load error:", e);
      setError("Unable to load shipment detail. Check login or server.");
      setShipment(null);
      setItems([]);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      await load();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function onAddEvent(e) {
    e.preventDefault();
    setMutating(true);
    setError("");
    setOkMsg("");

    try {
      const payload = {
        eventType,
        locationText: locationText.trim() || null,
        notes: notes.trim() || null,
      };

      const data = await adminLogisticsApi.addShipmentEvent(id, payload);
      if (!data || data.ok !== true) throw new Error("event_failed");

      setOkMsg("Event added.");
      setLocationText("");
      setNotes("");
      await load();
    } catch (e) {
      console.error("[AdminShipmentDetailPage] add event error:", e);
      setError("Unable to add event (server rejected or conflict).");
    } finally {
      setMutating(false);
    }
  }

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Shipment Detail</h1>
            <p className="ucx-admin-subtitle">
              <Link to="/logistics/shipments" className="ucx-link">
                ← Back to Shipments
              </Link>
            </p>
          </div>

          <div className="ucx-admin-header-right">
            <div className="ucx-admin-header-actions">
              <button type="button" className="ucx-btn ucx-btn-outline" onClick={load} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {error ? <div className="ucx-admin-status ucx-admin-status-error">{error}</div> : null}
        {okMsg ? <div className="ucx-admin-status ucx-admin-status-success">{okMsg}</div> : null}

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Header</div>

          {loading ? (
            <div className="ucx-admin-empty">Loading…</div>
          ) : !shipment ? (
            <div className="ucx-admin-empty">Not found.</div>
          ) : (
            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Shipment No</label>
                <div>{shipment.shipment_no || shipment.shipmentNo || "-"}</div>
              </div>
              <div className="ucx-admin-field">
                <label>Status</label>
                <div>{shipment.status || "-"}</div>
              </div>
              <div className="ucx-admin-field">
                <label>Created</label>
                <div>{fmtTS(shipment.created_at || shipment.createdAt)}</div>
              </div>

              <div className="ucx-admin-field">
                <label>Origin</label>
                <div>
                  Branch: {shipment.origin_branch_name || shipment.originBranchName || shipment.origin_branch_id || shipment.originBranchId || "-"}
                  <br />
                  Location:{" "}
                  {shipment.origin_location_name ||
                    shipment.originLocationName ||
                    shipment.origin_location_id ||
                    shipment.originLocationId ||
                    "-"}
                </div>
              </div>

              <div className="ucx-admin-field">
                <label>Destination</label>
                <div>
                  Branch:{" "}
                  {shipment.destination_branch_name ||
                    shipment.destinationBranchName ||
                    shipment.destination_branch_id ||
                    shipment.destinationBranchId ||
                    "-"}
                  <br />
                  Location:{" "}
                  {shipment.destination_location_name ||
                    shipment.destinationLocationName ||
                    shipment.destination_location_id ||
                    shipment.destinationLocationId ||
                    "-"}
                </div>
              </div>

              <div className="ucx-admin-field">
                <label>Transit</label>
                <div>
                  {shipment.transit_location_name ||
                    shipment.transitLocationName ||
                    shipment.transit_location_id ||
                    shipment.transitLocationId ||
                    "(none)"}
                </div>
              </div>

              <div className="ucx-admin-field">
                <label>Carrier</label>
                <div>{shipment.carrier_name || shipment.carrierName || "-"}</div>
              </div>

              <div className="ucx-admin-field">
                <label>Tracking Ref</label>
                <div>{shipment.tracking_ref || shipment.trackingRef || "-"}</div>
              </div>

              <div className="ucx-admin-field">
                <label>Shipped / Received</label>
                <div>
                  Shipped: {fmtTS(shipment.shipped_at || shipment.shippedAt)}
                  <br />
                  Received: {fmtTS(shipment.received_at || shipment.receivedAt)}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Items</div>

          <div className="ucx-admin-table-wrapper">
            <table className="ucx-admin-table">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Line</th>
                  <th style={{ width: 140 }}>Product ID</th>
                  <th style={{ width: 140 }}>Batch ID</th>
                  <th style={{ width: 120 }}>Qty</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="ucx-admin-empty">
                      Loading…
                    </td>
                  </tr>
                ) : normalizedItems.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="ucx-admin-empty">
                      No items.
                    </td>
                  </tr>
                ) : (
                  normalizedItems.map((it) => (
                    <tr key={it.id || `${it.line_no}-${it.product_id}`}>
                      <td>{it.line_no ?? it.lineNo ?? "-"}</td>
                      <td>{it.product_id ?? it.productId ?? "-"}</td>
                      <td>{it.batch_id ?? it.batchId ?? "-"}</td>
                      <td>{it.quantity ?? "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Add Event</div>

          <form className="ucx-admin-form" onSubmit={onAddEvent}>
            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Event Type *</label>
                <select value={eventType} onChange={(e) => setEventType(e.target.value)}>
                  <option value="CREATED">CREATED</option>
                  <option value="SHIPPED">SHIPPED</option>
                  <option value="IN_TRANSIT">IN_TRANSIT</option>
                  <option value="ARRIVED">ARRIVED</option>
                  <option value="RECEIVED">RECEIVED</option>
                  <option value="CANCELLED">CANCELLED</option>
                  <option value="NOTE">NOTE</option>
                </select>
              </div>

              <div className="ucx-admin-field" style={{ gridColumn: "span 2" }}>
                <label>Location text</label>
                <input value={locationText} onChange={(e) => setLocationText(e.target.value)} placeholder="Optional (e.g., Gaborone depot)" />
              </div>
            </div>

            <div className="ucx-admin-field">
              <label>Notes</label>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional note" />
            </div>

            <div className="ucx-admin-form-actions" style={{ marginTop: 12 }}>
              <button type="submit" className="ucx-btn ucx-btn-primary" disabled={mutating}>
                {mutating ? "Saving…" : "Add event"}
              </button>
            </div>
          </form>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Timeline</div>

          <div className="ucx-admin-table-wrapper">
            <table className="ucx-admin-table">
              <thead>
                <tr>
                  <th style={{ width: 190 }}>Time</th>
                  <th style={{ width: 140 }}>Type</th>
                  <th>Location</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="ucx-admin-empty">
                      Loading…
                    </td>
                  </tr>
                ) : normalizedEvents.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="ucx-admin-empty">
                      No events.
                    </td>
                  </tr>
                ) : (
                  normalizedEvents.map((ev) => (
                    <tr key={ev.id || `${ev.event_type}-${ev.created_at}`}>
                      <td>{fmtTS(ev.created_at || ev.createdAt)}</td>
                      <td>{ev.event_type || ev.eventType || "-"}</td>
                      <td>{ev.location_text || ev.locationText || "-"}</td>
                      <td>{ev.notes || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="ucx-admin-toolbar-hint" style={{ marginTop: 10 }}>
            If your backend transitions status based on events, use SHIPPED/IN_TRANSIT/ARRIVED/RECEIVED here.
          </div>
        </div>
      </div>
    </section>
  );
}

export default AdminShipmentDetailPage;
